import { useState } from "react";
import { X, ArrowLeft, Sparkles, CheckCircle2, Send, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PhoneInput, { formatPhone } from "@/components/PhoneInput";

const ACCESS_KEY = "lum_ai_access_token";

export function hasLuminousAccess() {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem(ACCESS_KEY);
}

/**
 * Lead-capture gate for Luminous AI brief generator.
 *
 * Behavior:
 *   - Until the user submits their details, brief generation is blocked.
 *   - On submit → POST /api/luminous/access-request, persist access_token in localStorage,
 *     show confirmation popup ("Our team will contact you for more details").
 *   - Confirmation has Close + Back buttons; closing also unlocks the page.
 *
 * Props:
 *   open    boolean
 *   onClose ()=>void
 *   onGrant ()=>void  (called after successful submission AND user dismisses confirmation)
 */
export default function LuminousAccessGate({ open, onClose, onGrant }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", requirement: "",
  });
  const [phone, setPhone] = useState({ iso: "IN", code: "+91", number: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.number.trim()) {
      setErr("Please enter a valid phone number with country code.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, phone: formatPhone(phone) };
      const { data } = await api.post("/luminous/access-request", payload);
      if (data.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
      setDone(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    }
    setBusy(false);
  };

  const dismiss = () => {
    onGrant?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--navy)]/85 backdrop-blur-sm p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Request Luminous AI access"
      data-testid="luminous-access-gate"
    >
      <button aria-label="Close" className="absolute inset-0 cursor-default" onClick={onClose} />

      <div className="relative bg-white w-full max-w-[920px] max-h-[92vh] flex flex-col border border-[var(--navy)] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-[var(--border)] bg-[var(--navy)] text-white">
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-[var(--gold)]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">Luminous AI · Access</div>
              <div className="font-display font-bold text-base sm:text-lg leading-tight mt-0.5">
                {done ? "Access request submitted" : "Request access to Luminous AI"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={done ? dismiss : onClose}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80 hover:text-[var(--gold)] inline-flex items-center gap-1.5 px-2 py-1"
              data-testid="luminous-gate-back-btn"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <button
              onClick={done ? dismiss : onClose}
              className="w-8 h-8 flex items-center justify-center border border-white/30 hover:bg-[var(--gold)] hover:border-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              aria-label="Close"
              data-testid="luminous-gate-close-btn"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {done ? (
            <div className="px-7 py-12 sm:py-16 text-center" data-testid="luminous-gate-confirmation">
              <CheckCircle2 size={48} className="mx-auto text-[var(--success)]" />
              <div className="eyebrow mt-6 text-[var(--success)]">Submission received</div>
              <h3 className="font-display font-black text-3xl sm:text-4xl mt-4 leading-tight text-[var(--navy)]">
                Thank you, {form.name.split(" ")[0] || "there"}.
              </h3>
              <p className="mt-5 text-[var(--slate)] max-w-md mx-auto leading-relaxed">
                {done.message || "Our analyst team will reach out within one business day with full access details."}
              </p>
              <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--slate-light)]">
                Reference: {done.id}
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <button
                  onClick={dismiss}
                  className="btn-primary"
                  data-testid="luminous-gate-continue-btn"
                >
                  Continue to preview <Sparkles size={13} />
                </button>
                <button onClick={dismiss} className="btn-secondary" data-testid="luminous-gate-confirm-close">
                  <ArrowLeft size={13} /> Back to site
                </button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-12 gap-0">
              {/* Pitch panel */}
              <div className="sm:col-span-5 bg-[var(--bg-2)] p-6 sm:p-7 border-r border-[var(--border)]">
                <div className="eyebrow">Why we ask</div>
                <h3 className="font-display font-bold text-2xl mt-3 leading-tight text-[var(--navy)]">
                  Access to Luminous AI is granted by our analyst desk.
                </h3>
                <p className="mt-4 text-sm text-[var(--slate)] leading-relaxed">
                  Luminous Intelligence™ produces decision-grade outputs — sourced and editorially reviewed.
                  We confirm every request to ensure briefs are matched to a real strategic question.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-[var(--navy)]">
                  {[
                    "Six structured executive brief types.",
                    "Built on our 15 Intelligence Modules.",
                    "Direct hand-off to a practice lead if you need a deeper engagement.",
                  ].map((p, i) => (
                    <li key={i} className="border-l-2 border-[var(--gold)] pl-3 leading-snug">{p}</li>
                  ))}
                </ul>
                <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">
                  Our team will contact you for more details.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={submit} className="sm:col-span-7 p-6 sm:p-7" data-testid="luminous-access-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="label-mono">Full name *</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-editorial" data-testid="lum-name" />
                  </div>
                  <div>
                    <label className="label-mono">Work email *</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-editorial" data-testid="lum-email" />
                  </div>
                  <div>
                    <PhoneInput
                      label="Phone (with country code)"
                      required
                      value={phone}
                      onChange={setPhone}
                      testIdPrefix="lum-phone"
                    />
                  </div>
                  <div>
                    <label className="label-mono">Company *</label>
                    <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-editorial" data-testid="lum-company" />
                  </div>
                  <div>
                    <label className="label-mono">Role (optional)</label>
                    <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-editorial" placeholder="e.g., Chief Strategy Officer" data-testid="lum-role" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-mono">What do you need from Luminous AI? *</label>
                    <textarea
                      required rows={4} value={form.requirement}
                      onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                      placeholder="e.g., A market-attractiveness snapshot on the EU specialty chemicals segment for our Q2 board pack."
                      className="input-editorial resize-none"
                      data-testid="lum-requirement"
                    />
                  </div>
                </div>

                {err && (
                  <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="lum-error">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span className="font-mono text-[12px]">{err}</span>
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button type="submit" disabled={busy} className="btn-primary" data-testid="lum-submit">
                    {busy ? "Submitting…" : <><Send size={13} /> Request access</>}
                  </button>
                  <button type="button" onClick={onClose} className="btn-ghost" data-testid="lum-cancel">
                    <ArrowLeft size={12} /> Cancel
                  </button>
                </div>
                <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)]">
                  Your details land in our practice inbox · We never share your information.
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
